import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

type Params = {
  mediaRef: any
  src: string | undefined
  initialAttributes?: any
}

export const useMediaControls = ({
  mediaRef,
  src,
  initialAttributes = {},
}: Params) => {
  const [mediaState, setMediaState] = useState<MediaState>('ended')
  const [canPlay, setCanPlay] = useState(false)
  const [muted, setMuted] = useState(mediaRef.current?.muted)
  const skipRef = useRef(false)
  const playPromiseRef = useRef()

  useLayoutEffect(() => {
    assignAttributes(mediaRef, initialAttributes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    assignAttributes(mediaRef, {
      muted: muted === undefined ? initialAttributes.muted : muted,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  useLayoutEffect(() => {
    const mediaElement = mediaRef.current
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.platform) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    if (mediaElement && src) {
      setMuted(mediaElement.muted)

      const onEnded = () => {
        setMediaState('ended')
      }
      const onPlaying = () => {
        if (skipRef.current) {
          return
        }
        setMediaState('playing')
      }
      const onPause = () => {
        setMediaState('paused')
      }
      const onLoadedData = () => {
        setCanPlay(mediaElement.readyState >= 2)
      }
      const onVolumeChange = () => {
        setMuted(mediaElement.muted)
      }
      mediaElement.addEventListener('ended', onEnded)
      mediaElement.addEventListener('playing', onPlaying)
      mediaElement.addEventListener('pause', onPause)
      mediaElement.addEventListener('loadeddata', onLoadedData)
      mediaElement.addEventListener('volumechange', onVolumeChange)

      // fix for ios safari. Loadeddata event only fires once play is initiated, causing the loading spinner to stay on.
      if (isIOS) {
        skipRef.current = true
        const onSuccess = () => {
          skipRef.current = false
          if (!mediaElement.autoplay) {
            setTimeout(() => {
              mediaElement.pause()
              mediaElement.currentTime = 0
            }, 1)
          }
        }
        mediaElement.play().then(onSuccess, () => {
          // try again muted, as the autoplay permission may have been rejected
          mediaElement.muted = true
          mediaElement.play().then(onSuccess)
        })
      }

      return () => {
        setMediaState('ended')
        mediaElement.removeEventListener('ended', onEnded)
        mediaElement.removeEventListener('playing', onPlaying)
        mediaElement.removeEventListener('pause', onPause)
        mediaElement.removeEventListener('loadeddata', onLoadedData)
        mediaElement.removeEventListener('volumechange', onVolumeChange)
      }
    }
  }, [mediaRef, src])

  useEffect(() => {
    const mediaElement = mediaRef.current
    if (mediaElement) {
      const onEnded = () => {
        mediaElement.currentTime = 0
      }
      mediaElement.addEventListener('ended', onEnded)

      return () => {
        mediaElement.removeEventListener('ended', onEnded)
      }
    }
  }, [mediaRef])

  const play = useCallback(
    (attributes = {}) => {
      if (mediaRef.current) {
        if (mediaState === 'ended') {
          mediaRef.current.currentTime = 0
        }
        assignAttributes(mediaRef, attributes)
        playPromiseRef.current = mediaRef.current.play().catch(() => {
          // try again muted, as the autoplay permission may have been rejected
          mediaRef.current.muted = true
          return mediaRef.current.play()
        })
      }
    },
    [mediaRef, mediaState],
  )

  const pause = useCallback(() => {
    if (playPromiseRef.current) {
      playPromiseRef.current.then(() => {
        mediaRef.current?.pause()
      })
    } else {
      mediaRef.current?.pause()
    }
  }, [mediaRef])

  const toggleMediaPlay = useCallback(() => {
    if (mediaState === 'playing') {
      pause()
    } else {
      play()
    }
  }, [mediaState, pause, play])

  const toggleMuted = useCallback(() => {
    if (mediaRef.current) {
      mediaRef.current.muted = !mediaRef.current.muted
    }
  }, [mediaRef])

  return {
    mediaState,
    toggleMediaPlay,
    canPlay,
    muted,
    toggleMuted,
    play,
    pause,
  }
}

type MediaState = 'ended' | 'playing' | 'paused'

const assignAttributes = (mediaRef: any, attributes: any) => {
  if (!mediaRef.current) {
    return
  }
  Object.keys(attributes).forEach((attribute) => {
    mediaRef.current[attribute] = attributes[attribute]
  })
}
